import { applyPromotion } from "./cdtLayout/restrictionRemedy";
import { namedSectionOf } from "./bayPartition";
import { emitReplicaRequest, finalizeCdts } from "./finalize";
import {
  effectiveAttrs,
  levelsForScope,
  type StudioDraft,
  type StudioItem,
  type StudioRunGeometry,
} from "./studioTypes";

/** Adopt the measured names before collapse so the persisted draft explains the release. */
export const adoptMeasuredBands = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[],
): StudioDraft => {
  const sectionAttr = levelsForScope(draft.spine, draft.scopedLevelOrder, [])[0]?.attr;
  if (!sectionAttr) throw new Error("The CDT Studio tree has no root attribute");
  if (draft.measuredBands) {
    emitReplicaRequest(items, runs, { sectionAttr, draft });
    return draft;
  }
  const original = finalizeCdts(items, draft, runs);
  const originalOrder = original.contract.valueOrder.__cdt0 ?? [];
  const { rules } = emitReplicaRequest(items, runs, {
    sectionAttr,
    draft,
    cdt0Order: originalOrder,
  });
  const itemOf = new Map(items.map((item) => [item.skuId, item]));
  const replacements = new Map<string, string[]>();
  const ownerOf = new Map<string, string>();
  const bandOfSku = new Map<string, string>();
  for (const stratum of rules.strata) {
    for (const skuId of stratum.skuIds) {
      const item = itemOf.get(skuId);
      if (!item) throw new Error(`Unknown measured product ${skuId}`);
      const parent = namedSectionOf(item, sectionAttr, draft);
      const owner = ownerOf.get(stratum.cdt0);
      if (owner !== undefined && owner !== parent) {
        throw new Error(`Measured name "${stratum.cdt0}" combines different draft groups`);
      }
      ownerOf.set(stratum.cdt0, parent);
      bandOfSku.set(skuId, stratum.cdt0);
      const names = replacements.get(parent) ?? [];
      if (!names.includes(stratum.cdt0)) names.push(stratum.cdt0);
      replacements.set(parent, names);
    }
  }
  for (const names of replacements.values()) {
    names.sort((a, b) => rules.cdt0Order.indexOf(a) - rules.cdt0Order.indexOf(b));
  }
  const order = [...new Set(originalOrder.flatMap((name) => replacements.get(name) ?? [name]))];
  if (rules.strata.some((stratum) => !order.includes(stratum.cdt0))) {
    throw new Error("A measured band has no position in the shipped root order");
  }
  let adopted = draft;
  for (const stratum of rules.strata) {
    const first = itemOf.get(stratum.skuIds[0]);
    if (!first) throw new Error("A measured band is empty");
    const parentValue = String(effectiveAttrs(first, draft)[sectionAttr] ?? "");
    if (stratum.cdt0 === namedSectionOf(first, sectionAttr, draft)) continue;
    adopted = applyPromotion(
      adopted,
      {
        fromAttr: stratum.bandAttr ?? sectionAttr,
        topAttr: sectionAttr,
        parentValue,
        bandVariant: stratum.cdt0,
        newValues: replacements.get(namedSectionOf(first, sectionAttr, draft)) ?? [],
        retag: Object.fromEntries(stratum.skuIds.map((skuId) => [skuId, stratum.cdt0])),
        valueOrder: order,
      },
      items,
    );
  }
  return {
    ...adopted,
    restrictions: adopted.restrictions.flatMap((restriction) => {
      const named = restriction.cdtPath.at(-1);
      const replacementsForRule = named === undefined ? undefined : replacements.get(named);
      if (!replacementsForRule || replacementsForRule.every((name) => name === named)) {
        return [restriction];
      }
      const groups = new Map<string, string[]>();
      for (const skuId of restriction.skuIds) {
        const band = bandOfSku.get(skuId);
        if (!band || !replacementsForRule.includes(band)) {
          throw new Error(`Restriction ${restriction.id} does not match its measured bands`);
        }
        const skus = groups.get(band) ?? [];
        skus.push(skuId);
        groups.set(band, skus);
      }
      return [...groups].map(([band, skuIds]) => ({
        ...restriction,
        id: `${restriction.id}:measured:${encodeURIComponent(band)}`,
        cdtPath: [...restriction.cdtPath.slice(0, -1), band],
        collapsedKey: null,
        skuIds,
      }));
    }),
    valueOrder: { ...adopted.valueOrder, [sectionAttr]: order },
    scopedValueOrder: { ...adopted.scopedValueOrder, "": order },
    measuredBands: { sectionAttr, strata: rules.strata },
  };
};
